import { createContext, type ReactNode, useContext, useMemo } from 'react';

import { useModifierKeyPressed } from './useModifierKeyPressed';

type AdminIdContextValue = {
  enabled: boolean;
};

const AdminIdContext = createContext<AdminIdContextValue>({
  enabled: false,
});

/**
 * Gates the entity-id reveal on selection rows. `enabled` comes from the app —
 * this library cannot read feature flags — and is deliberately the only thing
 * in context: keeping the modifier-key subscription in {@link useShowAdminId}
 * means a ⌘ press re-renders the rows that read it, not the whole app.
 *
 * Every tree and select reads it through that hook, so new ones inherit the
 * behaviour instead of opting in at the call site.
 */
export const AdminIdProvider = ({
  enabled,
  children,
}: {
  enabled: boolean;
  children: ReactNode;
}) => {
  const value = useMemo(() => ({ enabled }), [enabled]);

  return (
    <AdminIdContext.Provider value={value}>{children}</AdminIdContext.Provider>
  );
};

/** With the feature off, no key listener is registered at all. */
export const useShowAdminId = () => {
  const { enabled } = useContext(AdminIdContext);
  return useModifierKeyPressed(enabled);
};
